import { useTour } from '@reactour/tour';
import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { InfoPopover } from '@/components/common/info-popover';
import { useModal } from '@/components/common/modal';
import { PageLoadingSpinner } from '@/components/common/page-loading-spinner';
import { TopBar } from '@/components/common/top-bar';
import { Button, ButtonVariant } from '@/components/form/button';
import { Input } from '@/components/form/input';
import { getTourStep } from '@/features/demo/steps';
import { useRoute } from '@/hooks/use-route';
import { MeasurementOverviewLayout } from '@/layouts';
import { isResultReady } from '@/utils/is-result-ready';

import {
  ConcentrationPercentage,
  ViabilityPercentage,
  ViableConcentrationPercentage,
} from '../../single-measurement/components/percentage-cards';
import { getBatch } from '../../single-measurement/services/batches';
import { SingleMeasurmentQueryKeys } from '../../single-measurement/services/query-keys';
import { State } from '../../single-measurement/types';
import { PitchRateSettingsModal } from '../components/pitch-rate-settings-modal';
import { YeastVolumeItem } from '../components/yeast-volume-item';
import { PitchRateContext, PitchRateProvider } from '../provider/provider';
import { ActionKind, PlatoGravity } from '../provider/types';
import { getWortUnitSymbol } from '../utils/get-wort-unit-symbol';

const BBPitchRatePage = () => {
  const { t } = useTranslation();
  const { routeToAnalysis } = useRoute();
  const { batchId = '' } = useParams();
  const { isOpen, toggle } = useModal();
  const { state, dispatch } = useContext(PitchRateContext);
  const [concentrationError, setConcentrationError] = useState<FieldError | undefined>(
    undefined,
  );
  const [pitchRateError, setPitchRateError] = useState<FieldError | undefined>(undefined);

  const { isLoading, isFetched, data } = useQuery({
    queryKey: [SingleMeasurmentQueryKeys.GetABatch, batchId],
    queryFn: () => getBatch(batchId),
  });

  // demo
  const {
    isOpen: isTourOpen,
    setIsOpen: setIsTourOpen,
    setCurrentStep,
    currentStep,
    steps,
  } = useTour();
  const [wasTourOpen, setWasTourOpen] = useState<Boolean>(false);

  // a trick to wait until the data is loaded
  useEffect(() => {
    setWasTourOpen(isTourOpen); // remember the tour state
    setIsTourOpen(false); // temp turn off the tour, once data is loaded then we can set it open
  }, []);

  // set viab and conz in initial load
  useEffect(() => {
    if (data?.resultStat.viab)
      dispatch({ type: ActionKind.SetViability, payload: data?.resultStat.viab });
    if (data?.resultStat.conz)
      dispatch({ type: ActionKind.SetConcentration, payload: data?.resultStat.conz });

    // if tour was open, then we open the tour again
    if (wasTourOpen) {
      setIsTourOpen(true);
      setCurrentStep(getTourStep(steps, 'gOTK8sl'));
    }
  }, [data]);

  useEffect(() => {
    // auto fill in demo mode
    // we have delayed the input for better UX
    if (currentStep === getTourStep(steps, 'BA1Qa3z'))
      setTimeout(function () {
        dispatch({ type: ActionKind.SetWort, payload: 20 });
      }, 250);

    if (currentStep === getTourStep(steps, 'FzN3b0U'))
      setTimeout(function () {
        dispatch({ type: ActionKind.SetGravity, payload: 12 });
      }, 200);

    if (currentStep === getTourStep(steps, 'Ms3LgZC'))
      setTimeout(function () {
        dispatch({ type: ActionKind.SetPitchRate, payload: 0.9 });
      }, 200);
  }, [currentStep]);

  // target concentration validation
  useEffect(() => {
    const { targetConcentration, concentration } = state;
    if (targetConcentration && concentration && targetConcentration > concentration) {
      setConcentrationError({ type: 'max', message: t('pitcRate.concentrationError') });
    } else {
      setConcentrationError(undefined);
    }
  }, [state.targetConcentration]);

  // pitch rate error
  useEffect(() => {
    const { pitchRate } = state;
    if (pitchRate && pitchRate > 2) {
      setPitchRateError({
        type: 'max',
        message: 'Pitch rate should not be higher than 2',
      });
    } else if (pitchRate && pitchRate < 0.2) {
      setPitchRateError({
        type: 'min',
        message: 'Pitch rate should not be lower than 0.2',
      });
    } else {
      setPitchRateError(undefined);
    }
  }, [state.pitchRate]);

  const onInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type:
      | ActionKind.SetWort
      | ActionKind.Calculate
      | ActionKind.SetGravity
      | ActionKind.SetPitchRate
      | ActionKind.SetTargetConcentration,
  ) => {
    // @ts-expect-error
    dispatch({ type, payload: e.target.value });
  };

  const wortUnitSymbol = useMemo(
    () => getWortUnitSymbol(state.wortUnit),
    [state.wortUnit],
  );

  const handleOnSettingsChange = () => {
    dispatch({ type: ActionKind.Reset });
  };

  return (
    <>
      <PitchRateSettingsModal
        key={Date.now()}
        isOpen={isOpen}
        toggle={toggle}
        onSave={handleOnSettingsChange}
      />
      <MeasurementOverviewLayout>
        <MeasurementOverviewLayout.Title title="Pitch Calculator" />

        <TopBar onBack={() => routeToAnalysis(batchId)}>
          <div id="step-pitch-settings-btn">
            <Button
              onClick={toggle}
              disabled={!isFetched}
              variant={ButtonVariant.Link}
              classnames="flex items-center gap-2"
            >
              <span className={`text-sm font-semibold text-[#008a00]`}>
                Calculator Settings
              </span>
            </Button>
          </div>
        </TopBar>

        <MeasurementOverviewLayout.Body>
          {isLoading ? <PageLoadingSpinner /> : null}

          {isFetched &&
          [State.UploadCompleted, State.InProgress].includes(data!.state) ? (
            <PageLoadingSpinner>Analysis is still in progress..</PageLoadingSpinner>
          ) : null}

          {isFetched && data && isResultReady(data?.state) ? (
            <div className="flex w-full gap-16 mt-6">
              <div className="flex flex-col w-8/12 gap-8">
                <div id="step-wort-volume" className="flex items-center">
                  <label className="w-full text-lg text-gray-700" htmlFor="wort">
                    Wort Volume [{wortUnitSymbol}]
                  </label>
                  <Input
                    name="wort"
                    lang="de-DE"
                    type="number"
                    value={state.wort ?? ''}
                    className="pl-6 font-bold text-center w-60"
                    onChange={(e) => onInputChange(e, ActionKind.SetWort)}
                  />
                </div>

                <div id="step-gravity" className="flex items-center">
                  <label className="w-full text-lg text-gray-700" htmlFor="gravity">
                    {state.gravityType === PlatoGravity.Plato
                      ? 'Plato [°P]'
                      : 'Specific Gravity'}
                  </label>
                  <Input
                    lang="de-DE"
                    type="number"
                    name="gravity"
                    value={state.gravity ?? ''}
                    className="pl-6 font-bold text-center w-60"
                    onChange={(e) => onInputChange(e, ActionKind.SetGravity)}
                  />
                </div>

                <div id="step-pitch-rate-input" className="flex items-center">
                  <label className="w-full text-lg text-gray-700" htmlFor="wort">
                    Pitch Rate [million cells/(ml x °P)]
                  </label>
                  <div className="pt-1 pr-2">
                    <InfoPopover type="info">
                      <p className="p-3 text-center text-gray-700">
                        Recommended pitch rates for an Ale are usually in the range of 0.5
                        - 1 and for a Lager 1 - 1.2.
                      </p>
                    </InfoPopover>
                  </div>
                  <Input
                    lang="de-DE"
                    type="number"
                    name="pitchRate"
                    min={0.2}
                    max={2.0}
                    step={0.01}
                    value={state.pitchRate ?? ''}
                    className="pl-6 font-bold text-center w-60"
                    onChange={(e) => onInputChange(e, ActionKind.SetPitchRate)}
                    errors={{ pitchRate: pitchRateError }}
                  />
                </div>

                <div id="step-target-concentration" className="flex items-center">
                  <label className="w-full text-lg text-gray-700" htmlFor="wort">
                    Target Concentration [million cells/ml]
                  </label>
                  <Input
                    lang="de-DE"
                    type="number"
                    name="targetConcentration"
                    value={state.targetConcentration ?? ''}
                    className="pl-6 font-bold text-center w-60"
                    onChange={(e) => onInputChange(e, ActionKind.SetTargetConcentration)}
                    errors={{ targetConcentration: concentrationError }}
                  />
                </div>

                <div className="flex justify-end gap-6">
                  <Button
                    id="step-pitch-rate-calc-btn"
                    onClick={() => dispatch({ type: ActionKind.Calculate })}
                  >
                    Calculate
                  </Button>
                </div>

                <div>
                  <div className="flex py-2 font-bold text-gray-600 border-b-2">
                    <span className="flex-1">Volume of Yeast</span>
                    <span className="flex-1">Weight of Yeast</span>
                  </div>
                  <div className="flex mt-4 text-lg text-gray-700">
                    <div className="flex flex-col flex-1 gap-4">
                      <YeastVolumeItem unit="ml" volume={state.yeastVolume?.MILILITER} />
                      <YeastVolumeItem unit="l" volume={state.yeastVolume?.LITER} />
                      <YeastVolumeItem
                        unit="hl"
                        volume={state.yeastVolume?.HECTORLITER}
                      />
                      <YeastVolumeItem unit="gal" volume={state.yeastVolume?.GALLON} />
                      <YeastVolumeItem unit="bbl" volume={state.yeastVolume?.BARRELS} />
                    </div>
                    <div className="flex flex-col flex-1 gap-4">
                      <YeastVolumeItem unit="g" volume={state.yeastVolume?.GRAM} />
                      <YeastVolumeItem unit="kg" volume={state.yeastVolume?.KILOGRAM} />
                      <YeastVolumeItem unit="lb" volume={state.yeastVolume?.POUND} />
                      <YeastVolumeItem unit="oz" volume={state.yeastVolume?.OUNCE} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col w-4/12 gap-16">
                {data.resultStat.viab ? (
                  <>
                    <div className="flex">
                      <ViabilityPercentage viability={data.resultStat.viab} />
                      {data.resultStat.viab < 90 ? (
                        <div className="pt-1 pl-2">
                          <InfoPopover type="warning">
                            <p className="p-3 text-center text-red-800">
                              Viability too low for optimal pitch
                            </p>
                          </InfoPopover>
                        </div>
                      ) : null}
                    </div>
                    <ViableConcentrationPercentage
                      concentration={data.resultStat.conz}
                      viability={data.resultStat.viab}
                    />
                  </>
                ) : null}
                <div className="flex">
                  <ConcentrationPercentage concentration={data.resultStat.conz} />
                  {data.resultStat.conz < 100 ? (
                    <div className="pt-1 pl-2">
                      <InfoPopover type="warning">
                        <p className="p-3 text-center text-red-800">
                          Concentration might be too low for reasonable pitch
                        </p>
                      </InfoPopover>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </MeasurementOverviewLayout.Body>
      </MeasurementOverviewLayout>
    </>
  );
};

export const BBPitchRateCalculator = () => (
  <PitchRateProvider>
    <BBPitchRatePage />
  </PitchRateProvider>
);
